import { Link } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Seo from '@/components/base/Seo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import { principles, site, stats, testimonials } from '@/mocks/site';

const focuses = [
  {
    icon: 'ri-lightbulb-line',
    title: '我们相信什么',
    desc: '信息差是职场中成本最高的一类隐性支出。同样的岗位，知道规则与不知道规则的人，最终拿到的结果可能相差很多。',
  },
  {
    icon: 'ri-group-line',
    title: '我们为谁整理',
    desc: '正在比较 offer 的求职者、准备谈补偿的在职者，以及已经进入争议流程、需要知道下一步该做什么的人。',
  },
  {
    icon: 'ri-forbid-2-line',
    title: '我们不做什么',
    desc: '不发布未经核实的私人信息，不做人身攻击，不承接付费的正面宣传，也不以删帖作为任何形式的交易条件。',
  },
];

export default function AboutPage() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${origin}/about` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 职镜观察',
      description: site.intro,
      url: `${origin}/about`,
      inLanguage: 'zh-CN',
      publisher: { '@type': 'Organization', name: site.name, email: site.email },
    },
  ];

  return (
    <PageShell>
      <Seo
        title="关于我们 | 定位、内容原则与用户口碑 - 职镜观察"
        description="了解职镜观察的网站定位、发展理念与内容原则：我们如何核实线索、如何保护投稿人隐私、如何为当事方保留回应通道，以及读者对我们的真实反馈。"
        keywords="关于职镜观察,网站定位,内容原则,投稿隐私保护,职场信息平台"
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100 paper-grain">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />
          <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <p className="font-label text-xs tracking-[0.28em] text-primary-600">ABOUT {site.nameEn}</p>
              <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                关于职镜观察：做一个把线索核对清楚的观察者
              </h1>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {site.intro}
              </p>
              <p className="mt-4 font-heading text-base font-semibold text-primary-700 md:text-lg">
                「{site.slogan}」
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="h-52 w-full overflow-hidden rounded-lg md:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20editorial%20reading%20room%20with%20stacks%20of%20folders%20and%20a%20single%20desk%20lamp%2C%20corkboard%20with%20notes%20on%20the%20wall%2C%20no%20identifiable%20people%2C%20cinematic%20amber%20lighting%2C%20high%20detail&width=900&height=700&seq=about-zhijing-01&orientation=landscape"
                  alt="职镜观察的资料整理间，文件堆叠与墙面上的线索便签"
                  title="职镜观察 - 内容整理与线索核对"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="网站定位"
            title="我们想解决的是「不知道该信什么」"
            desc="职场信息往往碎片化、情绪化，也需要区分事实与感受。我们做的事情，是把两者分开整理。"
          />
          <div className="grid gap-5 md:grid-cols-3">
            {focuses.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-5 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="内容原则"
            title="四条不会因为流量而改变的做法"
            desc="观察类内容的价值来自可信度，以下四条是我们在处理每一条线索时的共同前提。"
          />
          <div className="grid gap-5 sm:grid-cols-2">
            {principles.map((item, index) => (
              <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    <span className="mr-2 text-primary-600">{String(index + 1).padStart(2, '0')}</span>
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-foreground-950">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label} className="border-l border-background-50/20 pl-5">
                <p className="font-heading text-2xl font-black text-accent-300 md:text-3xl">{item.value}</p>
                <p className="mt-2 font-label text-sm text-background-300">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="用户口碑"
            title="读者的用法，往往比我们想象的更具体"
            desc="下面是来自读者的部分反馈，我们希望这些用法能被更多人用上。"
          />
          <div className="grid gap-5 md:grid-cols-2">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-800 md:text-base">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block font-label text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="block font-label text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-background-200 bg-background-50 p-7 md:flex-row md:items-center md:p-10">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                想提供线索，或者需要说明某处内容有误？
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                我们会认真对待每一封来信。投稿与联系邮箱：{site.email}，{site.replyTime}。
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="whitespace-nowrap rounded-md bg-primary-600 px-5 py-3 text-center font-label text-sm text-background-50 transition-colors hover:bg-primary-700"
              >
                前往投稿
              </Link>
              <Link
                to="/insight"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-center font-label text-sm text-foreground-800 transition-colors hover:border-primary-400"
              >
                浏览观察
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}